/* eslint-disable */
// CriticalModal — the `critical` tier's surface. It blocks the app until the
// person acknowledges it.
//
// ⚠️ DELIBERATELY NOT BUILT ON DnaModalShell. That shell provides four close
// affordances (×, backdrop, Esc, Cancel) and this surface must provide none of
// them — building on it and then disabling its own features would be fighting the
// primitive. Standalone, and the brief says so explicitly.
//
// ⚠️ IT BLOCKS, BUT IT IS NEVER A TRAP. No Esc, no backdrop click, no ×: a
// critical announcement that can be waved away is not critical, and the
// acknowledgement stamp would mean nothing. The boundary is that `I understand`
// is ALWAYS enabled, always visible without scrolling, and always the way out.
// If a condition is ever added under which it is disabled, this stops being a
// block and becomes a trap.
//
// ⚠️ IT WAITS FOR THE FOREGROUND. If the composer, the drawer or any other dialog
// is open, this does not appear until that closes. A blocking dialog that steals
// focus mid-sentence loses the sentence, and the person then resents the message.
// Nothing is lost — it queues, one at a time, with a `1 of N` marker.

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import * as Sentry from '@sentry/react';
import { supabase } from '../../supabase';
import { useNotifications } from '../../hooks/useNotifications';
import { useNotificationsStore } from '../../stores/notificationsStore';
import { canOpenNotification, openNotification } from '../../utils/entityRoutes';
import styles from './severity.module.css';

const Warn = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 9v4" /><path d="M12 17h.01" />
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
  </svg>
);

function initials(name) {
  const parts = String(name || 'System').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'S';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function CriticalModal() {
  const { critical, senderNames, markRead, refetch } = useNotifications();
  const composerOpen = useNotificationsStore((s) => s.showComposer);
  const drawerOpen = useNotificationsStore((s) => s.showDrawer);
  const [otherDialogOpen, setOtherDialogOpen] = useState(false);

  const queued = critical.length > 0;

  // Poll for any OTHER dialog only while a critical is actually waiting — bounded,
  // and it costs nothing in the overwhelmingly common case of none queued. Reading
  // the DOM in an effect rather than during render keeps the render pure.
  useEffect(() => {
    if (!queued) { setOtherDialogOpen(false); return undefined; }
    // ⚠️ aria-hidden is not optional in this selector. The workspace drawer
    // (CatalogDrawer) is PERMANENTLY mounted with role="dialog" and merely slides
    // off-screen, marking itself aria-hidden="true" when closed. Without the
    // :not([aria-hidden="true"]) clause this matches forever, and the critical
    // modal waits for a dialog that is never going to close — i.e. it never
    // appears at all. Found in the browser; a green build says nothing about it.
    const check = () => setOtherDialogOpen(
      !!document.querySelector(
        '[role="dialog"]:not([data-test="critical-modal"]):not([aria-hidden="true"])',
      ),
    );
    check();
    const t = setInterval(check, 400);
    return () => clearInterval(t);
  }, [queued]);

  if (!queued) return null;
  if (composerOpen || drawerOpen || otherDialogOpen) return null;   // waits, never steals

  // Oldest first: a queue should drain in the order it arrived.
  const ordered = [...critical].reverse();
  const row = ordered[0];
  const n = row.notifications;
  const who = senderNames[n.created_by] || 'System';
  const showOpen = canOpenNotification(n);

  const acknowledge = async () => {
    const stamp = new Date().toISOString();
    // acknowledged_at AND read_at — the column grant covers both, and an
    // acknowledgement that left it unread would keep the badge lit forever.
    const { error } = await supabase
      .from('notification_recipients')
      .update({ acknowledged_at: stamp, read_at: stamp })
      .eq('id', row.id);
    if (error) {
      Sentry.captureException(error, { tags: { source: 'CriticalModal.acknowledge' } });
      return;
    }
    refetch();
  };

  const open = () => {
    if (openNotification(n)) { markRead(row.id); }
  };

  return createPortal(
    <div className={styles.sevRoot}>
      {/* No onClick on the backdrop. That is the point. */}
      <div className={styles.modalBackdrop} data-test="critical-backdrop">
        <div
          className={styles.modal}
          role="dialog"
          aria-modal="true"
          aria-label="Important announcement"
          data-test="critical-modal"
        >
          <div className={styles.mHead}>
            <span className={styles.mGlyph}><Warn /></span>
            <div className={styles.mHeadText}>
              <div className={styles.mTitle} dir="auto" data-test="critical-title">{n.title}</div>
              {ordered.length > 1 && (
                <div className={styles.mCount} data-test="critical-count">1 of {ordered.length}</div>
              )}
            </div>
          </div>

          {/* never truncated — this is the tier where the words matter */}
          {n.body && (
            <div className={styles.mBody} dir="auto" data-test="critical-body">{n.body}</div>
          )}

          <div className={styles.mSender}>
            <span className={styles.mAvatar} aria-hidden="true">{initials(who)}</span>
            <span className={styles.mSenderName}>{who}</span>
          </div>

          <div className={styles.mFoot}>
            {showOpen && (
              <button type="button" className={styles.mOpen} onClick={open} data-test="critical-open">
                Open
              </button>
            )}
            <button type="button" className={styles.mAck} onClick={acknowledge} data-test="critical-ack">
              I understand
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default CriticalModal;
